import React, { useEffect, useRef, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Config from './SidebarCards/Config';
import AgendaTemplates from './SidebarCards/AgendaTemplates';
import StandardModal from 'components/general/defaultModals/StandardModal';
import RightSidebar from 'components/navigation/RightSidebar';

export default function ConfigToolbar({ config, refreshConfig: getConfig }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [reloading, setReloading] = useState(false);
  const [isLockingOverdue, setIsLockingOverdue] = useState(false);
  const { user: authUser } = useSelector((state) => state.auth);
  const url = process.env.REACT_APP_URL;
  const isWebmaster = Boolean(authUser?.user?.isWebmaster);
  const toggleRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;

    function onPointerDown(e) {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      if (toggleRef.current && toggleRef.current.contains(e.target)) return;
      setMenuOpen(false);
    }

    function onKeyDown(e) {
      if (e.key === 'Escape') setMenuOpen(false);
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const userAcl = authUser?.user?.acl;
  if (!isWebmaster && (!userAcl || userAcl.toLowerCase() !== 'officer')) {
    return null;
  }

  const sections = [
    {
      key: 'config',
      label: 'Meetings Configuration',
      icon: 'bi bi-gear',
    },
    {
      key: 'templates',
      label: 'Agenda Templates',
      icon: 'bi bi-list-ol',
    },
  ];

  const handleLockOverdueMeetings = async () => {
    if (isLockingOverdue) return;
    if (!window.confirm('Lock all overdue meetings? Only webmasters will be able to unlock them afterwards.')) return;

    setMenuOpen(false);
    setIsLockingOverdue(true);
    try {
      const response = await fetch(`${url}/meetings?limit=0`, {
        headers: { Authorization: `Bearer ${authUser?.token || ''}` },
      });
      if (!response.ok) throw new Error('Failed to load meetings.');
      const payload = await response.json();
      const overdueMeetings = (Array.isArray(payload?.data) ? payload.data : [])
        .filter((meeting) => meeting?.isLocked && !meeting?.locked);

      if (overdueMeetings.length === 0) {
        toast.info('No overdue meetings need locking.', { position: 'bottom-left' });
        return;
      }

      let lockedCount = 0;
      // Locked one by one (not a bulk request) so a single failure doesn't block the rest.
      for (const meeting of overdueMeetings) {
        try {
          const patchResponse = await fetch(`${url}/meeting/${meeting._id}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authUser?.token || ''}`,
            },
            body: JSON.stringify({ locked: true }),
          });
          if (patchResponse.ok) lockedCount += 1;
        } catch {
          // continue with the next meeting even if this one fails
        }
      }

      toast.success(`Locked ${lockedCount} of ${overdueMeetings.length} overdue meeting${overdueMeetings.length === 1 ? '' : 's'}.`, { position: 'bottom-left' });
      window.dispatchEvent(new Event('meetings:refresh'));
    } catch (error) {
      toast.error(error.message || 'Failed to lock overdue meetings.', { position: 'bottom-left' });
    } finally {
      setIsLockingOverdue(false);
    }
  };

  const handleSectionClick = (sectionKey) => {
    setActiveSection(sectionKey);
    setShowModal(true);
    setMenuOpen(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setActiveSection(null);
  };

  const handleReloadModalContent = async () => {
    if (reloading) return;

    setReloading(true);
    try {
      setReloadTick((current) => current + 1);
    } finally {
      setReloading(false);
    }
  };

  let modalTitle = '';
  let modalBodyComponent = null;
  let modalHeaderActions = null;

  switch (activeSection) {
    case 'config':
      modalTitle = 'Meetings Configuration';
      modalBodyComponent = <Config config={config} getConfig={getConfig} onSaved={handleCloseModal} formId="meetings-config-form" />;
      break;
    case 'templates':
      modalTitle = 'Agenda Templates';
      modalBodyComponent = <AgendaTemplates reloadToken={reloadTick} />;
      modalHeaderActions = (
        <Button
          type="button"
          variant="link"
          className="standard-modal-collapse-btn"
          onClick={handleReloadModalContent}
          disabled={reloading}
          aria-label={`Reload ${modalTitle || 'modal'} content`}
          title={`Reload ${modalTitle || 'modal'} content`}
        >
          <i className={`bi bi-arrow-clockwise${reloading ? ' meeting-modal-reload-icon--spinning' : ''}`} aria-hidden="true" />
        </Button>
      );
      break;
    default:
      break;
  }

  return (
    <>
      <div ref={toggleRef} className="right-menu">
        <button
          className="right-menu-toggle"
          onClick={() => setMenuOpen(v => !v)}
          aria-label="Toggle meetings settings menu"
          title="Meetings Settings"
        >
          <i className="bi bi-sliders2"></i>
        </button>
      </div>
      <RightSidebar label="Meetings Settings" open={menuOpen} onClose={() => setMenuOpen(false)} panelRef={panelRef} keepOpenOnAction>
            <div className="right-menu-header">
              <span className="right-menu-title">Meetings Settings</span>
              <button className="right-menu-close" onClick={() => setMenuOpen(false)} aria-label="Close">
                &times;
              </button>
            </div>
            <div className="right-menu-items">
              {sections.map(section => (
                <button
                  key={section.key}
                  className="right-menu-item"
                  onClick={() => handleSectionClick(section.key)}
                >
                  <i className={section.icon}></i>
                  <span>{section.label}</span>
                </button>
              ))}
              {isWebmaster && (
                <>
                  <hr className="right-menu-divider" />
                  <button
                    className="right-menu-item"
                    onClick={handleLockOverdueMeetings}
                    disabled={isLockingOverdue}
                  >
                    <i className={`bi ${isLockingOverdue ? 'bi-hourglass-split' : 'bi-lock-fill'}`}></i>
                    <span>{isLockingOverdue ? 'Locking meetings...' : 'Lock Overdue Meetings'}</span>
                  </button>
                </>
              )}
            </div>
      </RightSidebar>

      {activeSection === 'templates' ? (
        <AgendaTemplates navigationParent="configuration" directOpen reloadToken={reloadTick} onClose={handleCloseModal} />
      ) : (
        <StandardModal
          navigationParent="configuration"
          show={showModal}
          handleClose={handleCloseModal}
          title={modalTitle}
          headerActions={modalHeaderActions}
          style={{ maxWidth: '95%', marginTop: '4vh' }}
          ModalBodyComponent={<Container>{modalBodyComponent}</Container>}
          ModalFooterComponent={(
            <>
              <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
              <Button variant="primary" type="submit" form="meetings-config-form">
                <i className='bi bi-floppy'></i> Save
              </Button>
            </>
          )}
        />
      )}
    </>
  );
}
